import { useHotkeySequences, type Hotkey } from "@tanstack/react-hotkeys"
import { toast } from "react-toastify"
import { getDisabledReason } from "~/components/sidebar/search/pageCommands"
import type { PageCommand } from "~/components/sidebar/search/types"
import { VIEW_SHORTCUTS } from "~/constants/hotkeys"
import { useUser } from "~/providers/userProvider"
import { isModalOpen } from "~/utils/helpers"
import { shortcutMeta } from "~/utils/shortcutCheatsheet"
import { useSearchCommandsStore } from "~/stores/search-commands-store"

type ViewShortcut = { sequence: readonly Hotkey[]; name: string }

const { openColumnChooser: _, ...savedViewShortcuts } = VIEW_SHORTCUTS
const SAVED_VIEW_SHORTCUTS: readonly ViewShortcut[] = Object.values(savedViewShortcuts)

/**
 * Runs the view command carrying each of `shortcuts`' sequences (saved-view ones by default): a
 * sub-list opens the command center inside it, a disabled command toasts its reason. No command on
 * the page → no-op. `getCommands` runs only when a sequence fires.
 */
export function useViewHotkeys(
  getCommands: () => readonly PageCommand[],
  enabled: boolean,
  shortcuts: readonly ViewShortcut[] = SAVED_VIEW_SHORTCUTS,
) {
  const { permissions } = useUser()
  const requestOpen = useSearchCommandsStore((s) => s.requestOpen)

  useHotkeySequences(
    shortcuts.map(({ sequence, name }) => ({
      sequence: [...sequence],
      options: { meta: shortcutMeta("Views", name) },
      callback: () => {
        if (isModalOpen()) return
        const command = getCommands().find((c) => c.shortcut === sequence)
        if (!command) return
        const disabledReason = getDisabledReason(command, permissions)
        if (disabledReason) toast.info(disabledReason)
        else if (command.subCommands) requestOpen([command.id])
        else command.run()
      },
    })),
    // Each caller acts only on commands it owns, so several handlers per sequence are expected.
    { enabled, ignoreInputs: true, conflictBehavior: "allow" },
  )
}
